import { format } from 'date-fns/format';
import { useParams } from 'react-router-dom';

import { AllowAccess } from '@features/auth/ControlAccess/AllowAccess/AllowAccess.tsx';
import { useControlAccess } from '@features/auth/hooks/useControlAccess.ts';
import { AISummaryButton } from '@features/campaigns/performance/components/AISummaryButton/AISummaryButton.tsx';
import { ExportReport } from '@features/campaigns/performance/components/ExportReport/ExportReport.tsx';
import ImpressionsDistribution from '@features/campaigns/performance/components/ImpressionsDistribution/ImpressionsDistribution.tsx';
import { TopCharts } from '@features/campaigns/performance/components/TopCharts/TopCharts.tsx';
import { AISummary } from '@features/campaigns/performance/containers/AISummary/AISummary.tsx';
import { GeneralMetrics } from '@features/campaigns/performance/containers/GeneralMetrics/GeneralMetrics.tsx';
import { useAiSummary } from '@features/campaigns/performance/hooks/useAiSummary.tsx';
import { useCampaignCountry } from '@features/campaigns/shared/providers/CampaignCountryProvider/useCampaignCountry.ts';
import {
  CampaignFormat,
  CampaignStatus,
  CampaignStatuses,
  CampaignType,
  CampaignTypes,
} from '@features/campaigns/shared/types/campaign.ts';
import { Permissions } from '@shared/consts/permissions.ts';

import styles from './Performance.module.scss';

interface PerformanceProps {
  campaignFormat?: CampaignFormat[];
  campaignStartDate?: string;
  campaignEndDate?: string;
  campaignName?: string;
  campaignStatus?: CampaignStatus;
  campaignType?: CampaignType;
}

const getDateRange = (campaignStartDate?: string, campaignEndDate?: string) => {
  const startDate = new Date(campaignStartDate as string);
  const formattedStartDate = format(startDate, 'dd/MM/yyyy');
  const endDate = new Date(campaignEndDate as string);
  const formattedEndDate = format(endDate, 'dd/MM/yyyy');

  return `${formattedStartDate} - ${formattedEndDate}`;
};

function Performance({
  campaignStartDate,
  campaignEndDate,
  campaignName,
  campaignStatus,
  campaignType,
}: PerformanceProps) {
  const { campaignId } = useParams();
  const dateRange = getDateRange(campaignStartDate, campaignEndDate);
  const { isUserAllowedToUseResource } = useControlAccess({
    requiredPermissions: [
      Permissions.CAMPAIGN_PERFORMANCE_SUMMARY_ALL,
      Permissions.CAMPAIGN_PERFORMANCE_SUMMARY_ASSIGNED_ADVERTISERS,
      Permissions.CAMPAIGN_PERFORMANCE_SUMMARY_OWNED,
    ],
  });
  const { isSpanishCampaign, isGermanCampaign } = useCampaignCountry();

  const aiSummary = useAiSummary({ campaignId, isUserAllowedToUseResource });
  const isCampaignCompleted = campaignStatus === CampaignStatuses.COMPLETED;
  const isCampaignSelfService = campaignType === CampaignTypes.SELF_SERVICE;
  const shouldPartiallyHideInteractiveMetrics = isSpanishCampaign || isGermanCampaign;

  if (!campaignId) {
    return null;
  }

  const canShowAISummaryTab = aiSummary.isSummaryLoading || aiSummary.isSummaryGenerated;

  return (
    <>
      <section className={styles.spaceBetween}>
        <div className={styles.dateRange} data-testid='campaign-date-range'>
          {dateRange}
        </div>
        <div>
          <ExportReport />
          <AllowAccess
            requiredPermissions={[
              Permissions.CAMPAIGN_PERFORMANCE_SUMMARY_ALL,
              Permissions.CAMPAIGN_PERFORMANCE_SUMMARY_ASSIGNED_ADVERTISERS,
              Permissions.CAMPAIGN_PERFORMANCE_SUMMARY_OWNED,
            ]}
          >
            <AISummaryButton
              triggerAiSummaryGeneration={aiSummary.triggerGenerateAiSummary}
              disabled={aiSummary.isSummaryGenerated || aiSummary.isSummaryLoading}
              isSummaryLoading={aiSummary.isSummaryLoading}
              isCampaignCompleted={isCampaignCompleted}
            />
          </AllowAccess>
        </div>
      </section>
      <AllowAccess
        requiredPermissions={[
          Permissions.CAMPAIGN_PERFORMANCE_SUMMARY_ALL,
          Permissions.CAMPAIGN_PERFORMANCE_SUMMARY_ASSIGNED_ADVERTISERS,
          Permissions.CAMPAIGN_PERFORMANCE_SUMMARY_OWNED,
        ]}
        additionalCondition={canShowAISummaryTab}
      >
        <AISummary
          summary={aiSummary.generatedSummary}
          generationDate={aiSummary.generationDate}
          isSummaryGenerated={aiSummary.isSummaryGenerated}
          isSummaryLoading={aiSummary.isSummaryLoading}
          campaignName={campaignName || ''}
        />
      </AllowAccess>
      <GeneralMetrics dateRange={dateRange} />
      <ImpressionsDistribution
        dateRange={dateRange}
        shouldPartiallyHideInteractiveMetrics={shouldPartiallyHideInteractiveMetrics}
      />
      <TopCharts isCampaignSelfService={isCampaignSelfService} />
    </>
  );
}

export default Performance;
